/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { formatDateTime } from '../../utils/formatters';
import {
  fetchUsageReportSummary,
  fetchUsageReport,
  fetchUserLoginHistory
} from '../../api/adminClient';

const HISTORY_PAGE_SIZE = 50;

const Reporting = () => {
  const [reportSummary, setReportSummary] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);
  const [nameSearch, setNameSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [historyUser, setHistoryUser] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    loadReportingData();
  }, [selectedDays]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const columns = [
    { key: 'user_name', label: 'User', type: 'string' },
    { key: 'total_logins', label: 'Logins', type: 'number' },
    { key: 'unique_login_days', label: 'Unique Days', type: 'number' },
    { key: 'total_clients_created', label: 'Clients Created', type: 'number' },
    { key: 'total_reports_created', label: 'Reports Created', type: 'number' },
    { key: 'last_login_at', label: 'Last Login', type: 'date' },
    { key: 'activity_status', label: 'Status', type: 'string' },
  ];

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const filteredReportData = useMemo(() => {
    return reportData.filter((user) => {
      if (nameSearch.trim()) {
        const needle = nameSearch.trim().toLowerCase();
        const name = (user.user_name || '').toLowerCase();
        const email = (user.user_email || '').toLowerCase();
        if (!name.includes(needle) && !email.includes(needle)) return false;
      }
      if (dateSearch) {
        if (!user.last_login_at) return false;
        const loginDate = new Date(user.last_login_at).toLocaleDateString('en-CA');
        if (loginDate !== dateSearch) return false;
      }
      return true;
    });
  }, [reportData, nameSearch, dateSearch]);

  const sortedReportData = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return filteredReportData;
    const column = columns.find((c) => c.key === key);
    const dir = direction === 'asc' ? 1 : -1;

    return [...filteredReportData].sort((a, b) => {
      const rawA = a[key];
      const rawB = b[key];
      const emptyA = rawA === null || rawA === undefined || rawA === '';
      const emptyB = rawB === null || rawB === undefined || rawB === '';
      if (emptyA && emptyB) return 0;
      if (emptyA) return 1;
      if (emptyB) return -1;

      if (column?.type === 'number') {
        return (Number(rawA) - Number(rawB)) * dir;
      }
      if (column?.type === 'date') {
        return (new Date(rawA).getTime() - new Date(rawB).getTime()) * dir;
      }
      return String(rawA).localeCompare(String(rawB)) * dir;
    });
  }, [filteredReportData, sortConfig]);

  const loadReportingData = async () => {
    setReportLoading(true);
    try {
      const [summaryResponse, dataResponse] = await Promise.all([
        fetchUsageReportSummary({ days: selectedDays }),
        fetchUsageReport({ days: selectedDays, userType: 'slp' })
      ]);
      
      setReportSummary(summaryResponse.data?.summary || null);
      setReportData(dataResponse.data?.data || []);
    } catch (error) {
      console.error('Failed to load reporting data:', error);
      setReportSummary(null);
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  };

  const loadLoginHistory = async (userId, page) => {
    setHistoryLoading(true);
    try {
      const response = await fetchUserLoginHistory(userId, { page, limit: HISTORY_PAGE_SIZE });
      setHistoryData(response.data);
      setHistoryPage(page);
    } catch (error) {
      console.error('Failed to load login history:', error);
      setHistoryData({ error: 'Failed to load login history' });
    } finally {
      setHistoryLoading(false);
    }
  };

  const openLoginHistory = (user) => {
    setHistoryUser(user);
    setHistoryData(null);
    loadLoginHistory(user.user_id, 1);
  };

  const closeLoginHistory = () => {
    setHistoryUser(null);
    setHistoryData(null);
    setHistoryPage(1);
  };

  const exportToCSV = () => {
    if (!sortedReportData.length) return;

    const headers = ['User ID', 'Name', 'Email', 'Total Logins', 'Unique Days', 'Clients Created', 'Reports Created', 'Last Login', 'Activity Status'];
    const rows = sortedReportData.map(user => [
      user.user_id,
      user.user_name || '',
      user.user_email || '',
      user.total_logins || 0,
      user.unique_login_days || 0,
      user.total_clients_created || 0,
      user.total_reports_created || 0,
      user.last_login_at ? formatDateTime(user.last_login_at) : 'Never',
      user.activity_status || 'Unknown'
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `slp-usage-report-${selectedDays}days-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="reporting-view">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-semibold mb-1">Usage Reporting</h1>
          <p className="text-muted mb-0">
            Track SLP user activity, engagement, and report generation metrics.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {reportSummary && (
        <div className="row g-4 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card-soft h-100">
              <span className="badge bg-primary mb-2">Total Users</span>
              <h2 className="display-6 fw-semibold">{reportSummary.total_users_with_logins || 0}</h2>
              <p className="text-muted mb-0">SLPs with login history</p>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card-soft h-100">
              <span className="badge bg-success mb-2">Active Users</span>
              <h2 className="display-6 fw-semibold">{reportSummary.active_users_30d || 0}</h2>
              <p className="text-muted mb-0">Active in last {selectedDays} days</p>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card-soft h-100">
              <span className="badge bg-info mb-2">Total Reports</span>
              <h2 className="display-6 fw-semibold">{reportSummary.total_reports_created || 0}</h2>
              <p className="text-muted mb-0">Reports generated</p>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card-soft h-100">
              <span className="badge bg-warning mb-2">Total Clients</span>
              <h2 className="display-6 fw-semibold">{reportSummary.total_clients_created || 0}</h2>
              <p className="text-muted mb-0">Clients created</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-soft mb-4">
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div className="d-flex gap-2 flex-wrap">
            <label className="text-muted fw-semibold me-2 align-self-center">Time Period:</label>
            <button 
              className={`btn btn-sm ${selectedDays === 30 ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
              onClick={() => setSelectedDays(30)}
            >
              30 Days
            </button>
            <button 
              className={`btn btn-sm ${selectedDays === 90 ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
              onClick={() => setSelectedDays(90)}
            >
              90 Days
            </button>
            <button 
              className={`btn btn-sm ${selectedDays === 365 ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
              onClick={() => setSelectedDays(365)}
            >
              1 Year
            </button>
            <button 
              className={`btn btn-sm ${selectedDays === null ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
              onClick={() => setSelectedDays(null)}
            >
              All Time
            </button>
          </div>
          <button
            className="btn btn-sm btn-success rounded-pill"
            onClick={exportToCSV}
            disabled={!sortedReportData.length}
          >
            Export to CSV
          </button>
        </div>
        <div className="d-flex flex-wrap gap-3 align-items-center mt-3">
          <input
            type="text"
            className="form-control form-control-sm rounded-pill shadow-sm"
            style={{ maxWidth: '260px' }}
            placeholder="Search by name or email…"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
          <input
            type="date"
            className="form-control form-control-sm rounded-pill shadow-sm"
            style={{ maxWidth: '200px' }}
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
            title="Filter by last login date"
          />
          {(nameSearch || dateSearch) && (
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill"
              onClick={() => { setNameSearch(''); setDateSearch(''); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="card-soft">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className="text-muted fw-semibold"
                    role="button"
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    {col.label}
                    <span className="ms-1">
                      {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    Loading usage data…
                  </td>
                </tr>
              ) : sortedReportData.length ? (
                sortedReportData.map((user) => (
                  <tr key={user.user_id}>
                    <td>
                      <div>
                        <h6 className="fw-semibold mb-1">{user.user_name || 'N/A'}</h6>
                        <p className="text-muted mb-0 small">{user.user_email}</p>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{user.total_logins || 0}</span>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{user.unique_login_days || 0}</span>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{user.total_clients_created || 0}</span>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{user.total_reports_created || 0}</span>
                    </td>
                    <td>
                      <small className="text-muted d-block">
                        {user.last_login_at ? formatDateTime(user.last_login_at) : 'Never'}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill mt-1 py-0"
                        onClick={() => openLoginHistory(user)}
                      >
                        Login History
                      </button>
                    </td>
                    <td>
                      <span className={`badge ${
                        user.activity_status?.includes('Active') ? 'bg-success' : 
                        user.activity_status?.includes('Recent') ? 'bg-warning' : 
                        'bg-secondary'
                      }`}>
                        {user.activity_status || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    {nameSearch || dateSearch ? 'No matching results.' : 'No usage data available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {historyUser && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Login History — {historyUser.user_name || historyUser.user_email}
                </h5>
                <button type="button" className="btn-close" onClick={closeLoginHistory}></button>
              </div>
              <div className="modal-body">
                {historyLoading && !historyData ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : historyData?.error ? (
                  <div className="alert alert-danger mb-0">{historyData.error}</div>
                ) : historyData ? (
                  <>
                    <p className="text-muted small mb-3">
                      {historyUser.user_email} · Lifetime: <strong>{historyData.total}</strong> logins on{' '}
                      <strong>{historyData.unique_login_days}</strong> days
                      {historyData.first_login_at ? ` · First login ${formatDateTime(historyData.first_login_at)}` : ''}
                    </p>
                    {historyData.logins?.length ? (
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th scope="col" className="text-muted fw-semibold">#</th>
                            <th scope="col" className="text-muted fw-semibold">Logged in</th>
                            <th scope="col" className="text-muted fw-semibold">Method</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData.logins.map((entry, idx) => (
                            <tr key={entry.id}>
                              <td className="text-muted">
                                {historyData.total - ((historyData.page - 1) * historyData.limit + idx)}
                              </td>
                              <td>{formatDateTime(entry.login_at)}</td>
                              <td>
                                <span className={`badge ${entry.method === 'OTP' ? 'bg-light text-dark' : 'bg-info'}`}>
                                  {entry.method}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-muted mb-0">No logins recorded.</p>
                    )}
                  </>
                ) : null}
              </div>
              {historyData && !historyData.error && historyData.total > historyData.limit && (
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={historyLoading || historyPage <= 1}
                    onClick={() => loadLoginHistory(historyUser.user_id, historyPage - 1)}
                  >
                    Newer
                  </button>
                  <span className="text-muted small">
                    Page {historyPage} of {Math.ceil(historyData.total / historyData.limit)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={historyLoading || historyPage * historyData.limit >= historyData.total}
                    onClick={() => loadLoginHistory(historyUser.user_id, historyPage + 1)}
                  >
                    Older
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reporting;
